import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { pricingTiers } from '../mock';
import { Award, Users, BookOpen, LogOut, Plus, Download, Calendar, Settings, Medal, Trophy, Star } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

import { apiFetch } from '../services/api';
const DashboardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { company, logout } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmployee, setNewEmployee] = useState({ name: '', email: '' });
  const [isAddingEmployee, setIsAddingEmployee] = useState(false);
  const [isBulkEnrollOpen, setIsBulkEnrollOpen] = useState(false);
  const [selectedEmployeesForBulk, setSelectedEmployeesForBulk] = useState([]);
  const [selectedCoursesForBulk, setSelectedCoursesForBulk] = useState([]);
  const [sendInvitations, setSendInvitations] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const companyName = company?.name || 'Ihre Firma';
  const isAdmin = company?.email?.toLowerCase() === 'elektrotechnik.seidel@outlook.com';

  // Daten vom Backend laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Mitarbeiter laden
      const empResponse = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers: { }
      });
      const empData = await empResponse.json();
      if (empData.success) {
        // Normalize employee data - ensure 'id' field exists (use _id as fallback)
        const normalizedEmployees = empData.employees.map(emp => ({
          ...emp,
          id: emp.id || emp._id,
          courses: emp.courses || []
        }));
        setEmployees(normalizedEmployees);
      }

      // Kurse laden
      const courseResponse = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/courses`);
      const courseData = await courseResponse.json();
      if (courseData.success) {
        // Normalize course data - ensure 'id' field exists
        const normalizedCourses = courseData.courses.map(course => ({
          ...course,
          id: course.id || course._id
        }));
        setCourses(normalizedCourses);
      }

      // Zertifikate laden
      const certResponse = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificates`, {
        headers: { }
      });
      const certData = await certResponse.json();
      if (certData.success) {
        setCertificates(certData.certificates || []);
      }

      // Badges laden - für alle Mitarbeiter UND für die Firma selbst
      try {
        const allBadges = [];
        
        // Laden der Badges für Mitarbeiter
        for (const emp of (empData.employees || [])) {
          const empId = emp.id || emp._id;
          const badgeRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz/badges/${empId}`, {
            headers: { }
          });
          const badgeData = await badgeRes.json();
          if (badgeData.success && badgeData.badges) {
            allBadges.push(...badgeData.badges.map(b => ({ ...b, employeeName: emp.name })));
          }
        }
        
        // Laden der Badges für die Firma selbst (falls die Firma direkt Kurse abschließt)
        const companyData = JSON.parse(localStorage.getItem('company') || '{}');
        const companyId = companyData._id || companyData.id;
        if (companyId) {
          const companyBadgeRes = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz/badges/${companyId}`, {
            headers: { }
          });
          const companyBadgeData = await companyBadgeRes.json();
          if (companyBadgeData.success && companyBadgeData.badges) {
            allBadges.push(...companyBadgeData.badges.map(b => ({ ...b, employeeName: companyData.name || 'Firma' })));
          }
        }
        
        setBadges(allBadges);
      } catch (badgeError) {
        console.error('Error loading badges:', badgeError);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleAddEmployee = async () => {
    if (newEmployee.name && newEmployee.email) {
      try {
        const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json' },
          body: JSON.stringify(newEmployee)
        });

        const data = await response.json();
        
        if (data.success) {
          // Normalize employee data
          const normalizedEmployee = {
            ...data.employee,
            id: data.employee.id || data.employee._id,
            courses: data.employee.courses || []
          };
          setEmployees([...employees, normalizedEmployee]);
          setNewEmployee({ name: '', email: '' });
          setIsAddingEmployee(false);
          toast({
            title: "Mitarbeiter hinzugefügt",
            description: `${newEmployee.name} wurde erfolgreich registriert`
          });
        } else {
          throw new Error(data.detail || 'Fehler beim Hinzufügen');
        }
      } catch (error) {
        console.error('Error adding employee:', error);
        toast({
          title: "Fehler",
          description: error.message || "Mitarbeiter konnte nicht hinzugefügt werden",
          variant: "destructive"
        });
      }
    }
  };

  const handleEnrollEmployee = async (employeeId, courseId) => {
    try {
      // API-Call zum Backend
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/enrollments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: employeeId,
          courseId: courseId,
          paymentMethod: 'digistore'
        })
      });

      const data = await response.json();

      if (data.success && data.paymentUrl) {
        toast({
          title: "Weiterleitung zur Zahlung",
          description: "Sie werden zu Digistore24 weitergeleitet..."
        });
        
        // Weiterleitung zur Zahlungsseite nach 2 Sekunden
        setTimeout(() => {
          window.location.href = data.paymentUrl;
        }, 2000);
      } else {
        throw new Error(data.message || 'Buchung fehlgeschlagen');
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      toast({
        title: "Buchung fehlgeschlagen",
        description: error.message || "Bitte versuchen Sie es erneut",
        variant: "destructive"
      });
    }
  };

  // Admin: Alle Kurse für einen Mitarbeiter freischalten
  const handleAdminActivateAll = async (employeeId) => {
    if (!isAdmin) return;
    
    setIsProcessing(true);
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/enrollments/admin/activate-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: employeeId,
          courseIds: courses.map(c => c.id || c._id)
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Kurse aktiviert",
          description: data.message
        });
        loadData(); // Reload data
      } else {
        throw new Error(data.detail || 'Aktivierung fehlgeschlagen');
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Bulk Enrollment für mehrere Mitarbeiter
  const handleBulkEnrollment = async () => {
    if (selectedEmployeesForBulk.length === 0 || selectedCoursesForBulk.length === 0) {
      toast({
        title: "Auswahl erforderlich",
        description: "Bitte wählen Sie mindestens einen Mitarbeiter und einen Kurs",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiFetch(`${process.env.REACT_APP_BACKEND_URL}/api/enrollments/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeIds: selectedEmployeesForBulk,
          courseIds: selectedCoursesForBulk,
          sendInvitations: sendInvitations
        })
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Großanmeldung erfolgreich",
          description: `${data.enrollments?.length || 0} Anmeldungen erstellt, ${data.emailsSent || 0} Einladungen versendet`
        });
        
        setIsBulkEnrollOpen(false);
        setSelectedEmployeesForBulk([]);
        setSelectedCoursesForBulk([]);
        
        // Weiterleitung zur Zahlung wenn Betrag > 0
        if (data.paymentUrl && data.totalPrice > 0) {
          toast({
            title: "Weiterleitung zur Zahlung",
            description: `Gesamtbetrag: €${data.totalPrice.toFixed(2)}`
          });
          setTimeout(() => {
            window.location.href = data.paymentUrl;
          }, 2000);
        } else {
          loadData();
        }
      } else {
        throw new Error(data.detail || 'Großanmeldung fehlgeschlagen');
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleEmployeeSelection = (empId) => {
    setSelectedEmployeesForBulk(prev => 
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const toggleCourseSelection = (courseId) => {
    setSelectedCoursesForBulk(prev => 
      prev.includes(courseId) ? prev.filter(id => id !== courseId) : [...prev, courseId]
    );
  };

  const selectAllEmployees = () => {
    if (selectedEmployeesForBulk.length === employees.length) {
      setSelectedEmployeesForBulk([]);
    } else {
      setSelectedEmployeesForBulk(employees.map(e => e.id || e._id));
    }
  };

  const selectAllCourses = () => {
    if (selectedCoursesForBulk.length === courses.length) {
      setSelectedCoursesForBulk([]);
    } else {
      setSelectedCoursesForBulk(courses.map(c => c.id || c._id));
    }
  };

  const calculateDiscount = () => {
    const tier = pricingTiers.find(t => employees.length >= t.min && employees.length <= t.max);
    return tier ? tier.discount : 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Header */}
      <header className="border-b border-yellow-600/20 bg-black/40 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/assets/smartvolt-logo.png" alt="Smartvolt Academy" className="h-12" />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-300">Willkommen, <span className="text-yellow-500">{companyName}</span></span>
            <Button 
              variant="ghost" 
              className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10"
              onClick={() => navigate('/admin')}
            >
              <Settings className="h-4 w-4 mr-2" /> Admin
            </Button>
            <Button variant="ghost" className="text-gray-300 hover:text-yellow-500 hover:bg-yellow-500/10" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> Abmelden
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <p className="text-white text-xl">Laden...</p>
          </div>
        ) : (
          <>
        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-400 text-sm">Mitarbeiter</p>
                  <p className="text-3xl font-bold text-white">{employees?.length || 0}</p>
                </div>
                <Users className="h-12 w-12 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-400 text-sm">Abgeschlossene Kurse</p>
                  <p className="text-3xl font-bold text-white">{employees?.reduce((acc, emp) => acc + (emp.courses?.length || 0), 0) || 0}</p>
                </div>
                <BookOpen className="h-12 w-12 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-400 text-sm">Ihr Rabatt</p>
                  <p className="text-3xl font-bold text-yellow-500">{calculateDiscount()}%</p>
                </div>
                <Award className="h-12 w-12 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="employees" className="w-full">
          <TabsList className="bg-gray-900 border-yellow-600/20">
            <TabsTrigger value="employees" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">Mitarbeiter</TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">Kurskatalog</TabsTrigger>
            <TabsTrigger value="certificates" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">Zertifikate</TabsTrigger>
            <TabsTrigger value="badges" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-500">
              <Trophy className="h-4 w-4 mr-1" /> Abzeichen
            </TabsTrigger>
          </TabsList>

          {/* Employees Tab */}
          <TabsContent value="employees">
            <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-white">Mitarbeiterverwaltung</CardTitle>
                    <CardDescription className="text-gray-400">Verwalten Sie Ihre Mitarbeiter und deren Schulungen</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {/* Großanmeldung Button */}
                    <Dialog open={isBulkEnrollOpen} onOpenChange={setIsBulkEnrollOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10">
                          <Users className="h-4 w-4 mr-2" /> Großanmeldung
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-gray-900 border-yellow-600/30 max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-yellow-500">Großanmeldung - Mehrere Mitarbeiter anmelden</DialogTitle>
                          <DialogDescription className="text-gray-400">
                            Wählen Sie Mitarbeiter und Kurse aus, um alle auf einmal anzumelden. Einladungen werden automatisch per E-Mail versendet.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-6 py-4">
                          {/* Employee Selection */}
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <Label className="text-white font-semibold">Mitarbeiter auswählen</Label>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={selectAllEmployees}
                                className="text-yellow-500 hover:text-yellow-400"
                              >
                                {selectedEmployeesForBulk.length === employees.length ? 'Keine auswählen' : 'Alle auswählen'}
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-700 rounded-lg p-2">
                              {employees.map(emp => (
                                <label key={emp.id || emp._id} className="flex items-center gap-3 p-2 hover:bg-gray-800 rounded cursor-pointer">
                                  <input 
                                    type="checkbox"
                                    checked={selectedEmployeesForBulk.includes(emp.id || emp._id)}
                                    onChange={() => toggleEmployeeSelection(emp.id || emp._id)}
                                    className="w-4 h-4 rounded border-yellow-500 text-yellow-500 focus:ring-yellow-500"
                                  />
                                  <span className="text-white">{emp.name}</span>
                                  <span className="text-gray-500 text-sm">{emp.email}</span>
                                </label>
                              ))}
                              {employees.length === 0 && (
                                <p className="text-gray-500 text-center py-2">Keine Mitarbeiter vorhanden</p>
                              )}
                            </div>
                            <p className="text-gray-400 text-sm mt-2">{selectedEmployeesForBulk.length} Mitarbeiter ausgewählt</p>
                          </div>

                          {/* Course Selection */}
                          <div>
                            <div className="flex justify-between items-center mb-3">
                              <Label className="text-white font-semibold">Kurse auswählen</Label>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={selectAllCourses}
                                className="text-yellow-500 hover:text-yellow-400"
                              >
                                {selectedCoursesForBulk.length === courses.length ? 'Keine auswählen' : 'Alle auswählen'}
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-700 rounded-lg p-2">
                              {courses.map(course => (
                                <label key={course.id || course._id} className="flex items-center justify-between p-2 hover:bg-gray-800 rounded cursor-pointer">
                                  <div className="flex items-center gap-3">
                                    <input 
                                      type="checkbox"
                                      checked={selectedCoursesForBulk.includes(course.id || course._id)}
                                      onChange={() => toggleCourseSelection(course.id || course._id)}
                                      className="w-4 h-4 rounded border-yellow-500 text-yellow-500 focus:ring-yellow-500"
                                    />
                                    <span className="text-white">{course.title}</span>
                                  </div>
                                  <span className="text-yellow-500 font-semibold">€{course.price}</span>
                                </label>
                              ))}
                            </div>
                            <p className="text-gray-400 text-sm mt-2">{selectedCoursesForBulk.length} Kurse ausgewählt</p>
                          </div>

                          {/* Send Invitations Checkbox */}
                          <label className="flex items-center gap-3 p-3 border border-gray-700 rounded-lg cursor-pointer hover:bg-gray-800">
                            <input 
                              type="checkbox"
                              checked={sendInvitations}
                              onChange={(e) => setSendInvitations(e.target.checked)}
                              className="w-4 h-4 rounded border-yellow-500 text-yellow-500 focus:ring-yellow-500"
                            />
                            <div>
                              <span className="text-white">Einladungen per E-Mail versenden</span>
                              <p className="text-gray-500 text-sm">Alle ausgewählten Mitarbeiter erhalten eine Einladung zur Schulung</p>
                            </div>
                          </label>

                          {/* Summary */}
                          <div className="bg-gray-800 p-4 rounded-lg">
                            <h4 className="text-yellow-500 font-semibold mb-2">Zusammenfassung</h4>
                            <div className="space-y-1 text-sm">
                              <p className="text-gray-400">Anmeldungen: <span className="text-white">{selectedEmployeesForBulk.length * selectedCoursesForBulk.length}</span></p>
                              <p className="text-gray-400">Geschätzter Preis: <span className="text-yellow-500 font-semibold">
                                €{(selectedCoursesForBulk.reduce((sum, cId) => {
                                  const course = courses.find(c => (c.id || c._id) === cId);
                                  return sum + (course?.price || 0);
                                }, 0) * selectedEmployeesForBulk.length * (1 - calculateDiscount() / 100)).toFixed(2)}
                              </span></p>
                              {calculateDiscount() > 0 && (
                                <p className="text-green-500 text-xs">Rabatt: {calculateDiscount()}%</p>
                              )}
                            </div>
                          </div>

                          <Button 
                            onClick={handleBulkEnrollment}
                            disabled={isProcessing || selectedEmployeesForBulk.length === 0 || selectedCoursesForBulk.length === 0}
                            className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                          >
                            {isProcessing ? 'Wird verarbeitet...' : 'Großanmeldung durchführen'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Dialog open={isAddingEmployee} onOpenChange={setIsAddingEmployee}>
                      <DialogTrigger asChild>
                        <Button className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400">
                          <Plus className="h-4 w-4 mr-2" /> Mitarbeiter hinzufügen
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-gray-900 border-yellow-600/20">
                        <DialogHeader>
                          <DialogTitle className="text-white">Neuer Mitarbeiter</DialogTitle>
                          <DialogDescription className="text-gray-400">Fügen Sie einen neuen Mitarbeiter hinzu</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label htmlFor="empName" className="text-gray-300">Name</Label>
                            <Input
                              id="empName"
                              value={newEmployee.name}
                              onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                              className="bg-gray-800 border-yellow-600/20 text-white"
                              placeholder="Max Mustermann"
                            />
                          </div>
                          <div>
                            <Label htmlFor="empEmail" className="text-gray-300">E-Mail</Label>
                            <Input
                              id="empEmail"
                              type="email"
                              value={newEmployee.email}
                              onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                              className="bg-gray-800 border-yellow-600/20 text-white"
                              placeholder="max@firma.de"
                            />
                          </div>
                          <Button className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400" onClick={handleAddEmployee}>
                            Hinzufügen
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {employees && employees.length > 0 ? employees.map(employee => (
                    <div key={employee.id} className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-white font-semibold">{employee.name}</h3>
                          <p className="text-gray-400 text-sm">{employee.email}</p>
                          <p className="text-gray-500 text-sm mt-2">Abgeschlossene Kurse: {employee.courses.length}</p>
                        </div>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10">
                              Zu Kurs anmelden
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-gray-900 border-yellow-600/20 max-w-2xl">
                            <DialogHeader>
                              <DialogTitle className="text-white">Kurs auswählen für {employee.name}</DialogTitle>
                              <DialogDescription className="text-gray-400">Wählen Sie eine Schulung aus</DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 mt-4 max-h-96 overflow-y-auto">
                              {courses && courses.length > 0 ? courses.map(course => (
                                <div key={course.id} className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h4 className="text-white font-semibold">{course.title}</h4>
                                      <p className="text-gray-400 text-sm">{course.duration}</p>
                                      <p className="text-yellow-500 font-bold mt-2">€{course.price}</p>
                                    </div>
                                    <Button 
                                      size="sm" 
                                      className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400"
                                      onClick={() => handleEnrollEmployee(employee.id, course.id)}
                                    >
                                      Buchen
                                    </Button>
                                  </div>
                                </div>
                              )) : (
                                <p className="text-gray-400 text-center py-4">Keine Kurse verfügbar</p>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                        
                        {/* Admin: Alle Kurse freischalten */}
                        {isAdmin && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                            onClick={() => handleAdminActivateAll(employee.id)}
                            disabled={isProcessing}
                          >
                            {isProcessing ? '...' : '✓ Alle freischalten'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-400 text-center py-8">Keine Mitarbeiter vorhanden. Fügen Sie einen hinzu!</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses && courses.length > 0 ? courses.map(course => (
                <Card key={course.id} className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20 hover:border-yellow-500/40 transition-all duration-300 overflow-hidden">
                  {/* Course Icon/Thumbnail */}
                  {course.icon && (
                    <div className="relative h-32 w-full overflow-hidden">
                      <img 
                        src={course.icon} 
                        alt={course.title}
                        className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
                    </div>
                  )}
                  <CardHeader className={course.icon ? 'pt-2' : ''}>
                    <span className="text-xs font-semibold text-yellow-500 bg-yellow-500/10 px-3 py-1 rounded-full w-fit">{course.category}</span>
                    <CardTitle className="text-white text-lg mt-2">{course.title}</CardTitle>
                    <CardDescription className="text-gray-400 line-clamp-2">{course.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-gray-400 text-sm">{course.duration}</span>
                      <span className="text-2xl font-bold text-yellow-500">€{course.price}</span>
                    </div>
                    <Button className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-semibold hover:from-yellow-500 hover:to-yellow-400" onClick={() => navigate(`/courses/${course.id}`)}>
                      Details ansehen
                    </Button>
                  </CardContent>
                </Card>
              )) : (
                <p className="text-gray-400 text-center py-8">Keine Kurse verfügbar</p>
              )}
            </div>
          </TabsContent>

          {/* Certificates Tab */}
          <TabsContent value="certificates">
            <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
              <CardHeader>
                <CardTitle className="text-white">Zertifikate</CardTitle>
                <CardDescription className="text-gray-400">Alle ausgestellten Zertifikate Ihrer Mitarbeiter ({certificates.length} Zertifikate)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {certificates.length > 0 ? certificates.map(cert => (
                    <div key={cert._id || cert.certificateNumber} className="border border-yellow-600/20 rounded-lg p-4 bg-gray-800/50">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-4">
                          <Award className="h-10 w-10 text-yellow-500" />
                          <div>
                            <h3 className="text-white font-semibold">{cert.courseTitle}</h3>
                            <p className="text-gray-400 text-sm">{cert.employeeName}</p>
                            <p className="text-gray-500 text-sm">Zertifikat-Nr.: {cert.certificateNumber}</p>
                            <div className="flex gap-4 mt-2 text-xs">
                              <span className="text-gray-400">
                                Ausgestellt: {new Date(cert.issueDate).toLocaleDateString('de-DE')}
                              </span>
                              <span className="text-yellow-500 flex items-center gap-1">
                                <Calendar className="h-3 w-3" /> 
                                Gültig bis: {new Date(cert.expiryDate).toLocaleDateString('de-DE')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <span className={`text-xs px-2 py-1 rounded text-center ${
                            new Date(cert.expiryDate) > new Date() 
                              ? 'bg-green-500/20 text-green-500' 
                              : 'bg-red-500/20 text-red-500'
                          }`}>
                            {new Date(cert.expiryDate) > new Date() ? 'Gültig' : 'Abgelaufen'}
                          </span>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                            onClick={() => {
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${cert._id}/download`, '_blank');
                            }}
                          >
                            <Download className="h-4 w-4 mr-2" /> PDF
                          </Button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-400 text-center py-8">Noch keine Zertifikate vorhanden</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges">
            <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Trophy className="h-6 w-6 text-yellow-500" />
                  Abzeichen & Auszeichnungen
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Verdiente Abzeichen für erfolgreich abgeschlossene Schulungen
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Badge Summary */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-yellow-600/20 to-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                    <Medal className="h-10 w-10 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-400">{badges.filter(b => b.badgeType === 'gold').length}</p>
                    <p className="text-sm text-gray-400">Gold</p>
                  </div>
                  <div className="bg-gradient-to-br from-gray-400/20 to-gray-300/10 border border-gray-400/30 rounded-lg p-4 text-center">
                    <Medal className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-gray-300">{badges.filter(b => b.badgeType === 'silver').length}</p>
                    <p className="text-sm text-gray-400">Silber</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-700/20 to-amber-600/10 border border-amber-600/30 rounded-lg p-4 text-center">
                    <Medal className="h-10 w-10 text-amber-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-amber-600">{badges.filter(b => b.badgeType === 'bronze').length}</p>
                    <p className="text-sm text-gray-400">Bronze</p>
                  </div>
                </div>

                {/* Badge List */}
                <div className="space-y-4">
                  {badges.length > 0 ? badges.map((badge, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700/50 hover:border-yellow-500/30 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${
                          badge.badgeType === 'gold' 
                            ? 'bg-yellow-500/20' 
                            : badge.badgeType === 'silver' 
                              ? 'bg-gray-400/20' 
                              : 'bg-amber-600/20'
                        }`}>
                          <Medal className={`h-8 w-8 ${
                            badge.badgeType === 'gold' 
                              ? 'text-yellow-400' 
                              : badge.badgeType === 'silver' 
                                ? 'text-gray-300' 
                                : 'text-amber-600'
                          }`} />
                        </div>
                        <div>
                          <p className="text-white font-semibold">{badge.badgeName || badge.courseTitle}</p>
                          <p className="text-gray-400 text-sm">
                            {badge.employeeName} • Ergebnis: {badge.score?.toFixed(0)}%
                          </p>
                          <p className="text-gray-500 text-xs">
                            Erhalten am: {badge.issuedAt ? new Date(badge.issuedAt).toLocaleDateString('de-DE') : 'Unbekannt'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          badge.badgeType === 'gold' 
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' 
                            : badge.badgeType === 'silver' 
                              ? 'bg-gray-400/20 text-gray-300 border border-gray-400/30' 
                              : 'bg-amber-600/20 text-amber-500 border border-amber-600/30'
                        }`}>
                          {badge.badgeType === 'gold' ? 'Gold' : badge.badgeType === 'silver' ? 'Silber' : 'Bronze'}
                        </span>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-12">
                      <Trophy className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400 text-lg mb-2">Noch keine Abzeichen vorhanden</p>
                      <p className="text-gray-500 text-sm">Schließen Sie Schulungen erfolgreich ab, um Abzeichen zu erhalten</p>
                      <Button 
                        className="mt-4 bg-yellow-500 text-black hover:bg-yellow-400"
                        onClick={() => navigate('/courses')}
                      >
                        Kurse entdecken
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
